import React, {
  FunctionComponent,
  Dispatch,
  SetStateAction,
  useState,
  ChangeEvent,
} from "react";
import {
  useTranslate,
  isValidDate,
  dateValidator,
  limitDateInputValueLength,
} from "../../../../helpers";
import { TooltipWithClose } from "../../../../components";
import { Choice, DateField, Alert } from "@cmsgov/ds-medicare-gov";
import { Plan, EnrollData2023, MCTDate } from "../../../../@types/index";
import { useFlags } from "launchdarkly-react-client-sdk";

import { isWithinInterval, endOfDay } from "date-fns";
import { SpecialEnrollmentReason } from "./SpecialEnrollmentReason";
import { showSEPReason } from "./helpers";

export const SpecialEnrollmentChoice: FunctionComponent<{
  reason: SpecialEnrollmentReason;
  setEnrollData: Dispatch<SetStateAction<EnrollData2023>>;
  enrollData: EnrollData2023;
  formSubmitted: boolean;
  plan: Plan;
  alertFormError?: boolean;
}> = ({
  reason,
  setEnrollData,
  enrollData,
  formSubmitted,
  plan,
  alertFormError = false,
}) => {
  const { day, month, year } = enrollData.sepReasons.find(
    r => r.key === reason.label
  )?.date || { day: "", month: "", year: "" };
  const t = useTranslate();
  const flags = useFlags();
  const [dateValues, setDateValues] = useState<{
    day: string;
    month: string;
    year: string;
  }>({ day, month, year });
  const [dayMarkedInvalid, markDayInvalid] = useState(false);
  const [monthMarkedInvalid, markMonthInvalid] = useState(false);
  const [yearMarkedInvalid, markYearInvalid] = useState(false);

  const {
    alertLabel,
    label,
    reasonCode,
    requiresDate,
    dateLabel,
    showAlertDates,
    tooltip,
    ariaLabel,
  } = reason;

  const displayChoice = showSEPReason(flags, plan, reason);

  const [isChecked, setChecked] = React.useState(false);
  React.useEffect(() => {
    setChecked(!!enrollData.sepReasons.find(r => r.key === label));
  }, []);

  let showAlert = !!alertLabel;

  if (showAlert && showAlertDates) {
    const {
      month: startMonth,
      day: startDay,
      year: startYear,
    } = showAlertDates.start;
    const { month: endMonth, day: endDay, year: endYear } = showAlertDates.end;
    const today = new Date();
    const start = new Date(
      startYear || today.getFullYear(),
      startMonth - 1,
      startDay
    );
    const end = endOfDay(
      new Date(endYear || today.getFullYear(), endMonth - 1, endDay)
    );

    showAlert = isWithinInterval(today, { start, end });
  }

  const savedReason = enrollData.sepReasons.find(r => r.key === label);

  let dateErrorMessage = null;

  const monthName = `month.${label}`;
  const dayName = `day.${label}`;
  const yearName = `year.${label}`;

  if (formSubmitted && savedReason && savedReason.requiresDate) {
    dateErrorMessage =
      !savedReason.date || !isValidDate(savedReason.date)
        ? t("sep_form.enter_valid_date")
        : null;
    const { validDay, validMonth, validYear } = dateValidator(dateValues);
    if ((!validDay && !dayMarkedInvalid) || (validDay && dayMarkedInvalid))
      markDayInvalid(!validDay);
    if (
      (!validMonth && !monthMarkedInvalid) ||
      (validMonth && monthMarkedInvalid)
    )
      markMonthInvalid(!validMonth);
    if ((!validYear && !yearMarkedInvalid) || (validYear && yearMarkedInvalid))
      markYearInvalid(!validYear);
  }

  return displayChoice ? (
    <Choice
      type="checkbox"
      id={`checkbox_${label}`}
      name={t(label)}
      {...(ariaLabel ? { "aria-label": t(ariaLabel) } : {})}
      className="ds-u-margin-bottom--2"
      value={reasonCode}
      uncheckedChildren={
        <div className="ds-u-margin-left--5">
          {tooltip && (
            <TooltipWithClose
              title={t(tooltip.text)}
              headerText={t(tooltip.title)}
            >
              {t(tooltip.triggerText)}
            </TooltipWithClose>
          )}
        </div>
      }
      checkedChildren={
        (showAlert || requiresDate || tooltip) && (
          // do not display bottom margin when there is only tooltip and no alert text and date field
          <div
            className={
              !requiresDate && !showAlert
                ? "ds-u-margin-left--5"
                : "ds-u-margin-left--5 ds-u-margin-bottom--4"
            }
          >
            {tooltip && (
              <TooltipWithClose
                className={alertLabel ? "ds-u-margin-bottom--2" : ""}
                headerText={t(tooltip.title)}
                title={t(tooltip.text)}
              >
                {t(tooltip.triggerText)}
              </TooltipWithClose>
            )}

            {showAlert && alertLabel && (
              <Alert role="alert" hideIcon={true}>
                {t(alertLabel)}
              </Alert>
            )}

            {requiresDate && dateLabel && (
              <DateField
                errorMessage={dateErrorMessage}
                errorMessageClassName={
                  alertFormError ? "" : "ds-u-display--none"
                }
                dateFormatter={dateValues => ({
                  day: limitDateInputValueLength({
                    inputVal: dateValues.day,
                    limit: 2,
                  }),
                  month: limitDateInputValueLength({
                    inputVal: dateValues.month,
                    limit: 2,
                  }),
                  year: limitDateInputValueLength({
                    inputVal: dateValues.year,
                    limit: 4,
                  }),
                })}
                monthName={monthName}
                dayName={dayName}
                yearName={yearName}
                monthValue={dateValues.month}
                dayValue={dateValues.day}
                yearValue={dateValues.year}
                monthInvalid={monthMarkedInvalid}
                dayInvalid={dayMarkedInvalid}
                yearInvalid={yearMarkedInvalid}
                onChange={(
                  _: ChangeEvent<HTMLInputElement>,
                  date: MCTDate
                ): void => {
                  setDateValues(date);

                  setEnrollData({
                    ...enrollData,
                    sepReasons: enrollData.sepReasons.map(r => {
                      if (r.key === label) {
                        r.date = date;
                      }

                      return r;
                    }),
                  });
                }}
                label={t(dateLabel)}
                hint={t("dob.hint")}
                dayLabel={t("dob.day_label")}
                monthLabel={t("dob.month_label")}
                yearLabel={t("dob.year_label")}
              />
            )}
          </div>
        )
      }
      checked={isChecked}
      onChange={(): void => {
        // check if the reason associated with the checkbox is in enrollData.
        const index = enrollData.sepReasons.findIndex(r => r.key === label);

        if (index >= 0) {
          // reason is already in the enrollData, remove it (i.e. uncheck)
          setEnrollData({
            ...enrollData,
            sepReasons: enrollData.sepReasons.filter(r => r.key !== label),
          });
          setChecked(false);
        } else {
          // reason is not in the enrollData, add to enrollData.sepReasons, (i.e. check)
          setEnrollData({
            ...enrollData,
            sepReasons: [
              ...enrollData.sepReasons,
              { code: reasonCode, key: label, requiresDate },
            ],
          });
          setChecked(true);
        }
      }}
    >
      {t(label)}
    </Choice>
  ) : null;
};
